import React, { useState } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  DatePicker,
  Select,
  message,
  Tag,
  Timeline,
  Empty,
  Space,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  RollbackOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Download, Search, RefreshCcw, RotateCcw, History, ClipboardList, ShieldAlert, Truck, ChevronRight } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { FetchReturnedVehiclesReport_api } from "../../Api/Stock/stock_api";
import { FetchVTMMappings_api } from "../../Api/Vehicles/vehicle_api";
import { FetchDealers_api } from "../../Api";
import { downloadExcel } from "../../lib/excelGenerator";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { usePagePermission } from "../../Hook";
import * as XLSX from "xlsx";

const { RangePicker } = DatePicker;
const { Option } = Select;

export const ReturnedVehiclesReport = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    "returned_vehicle_report" as any,
    ["admin", "cashier", "manager"],
  );
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<any>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // Fetch report data
  const {
    data: reportData,
    refetch: refetchReport,
    isFetching: isFetchingReport,
  } = useQuery({
    queryKey: ["returned_vehicles_report", filters],
    queryFn: () =>
      FetchReturnedVehiclesReport_api(
        filters.startDate,
        filters.endDate,
        filters.modelId,
        filters.dealerId,
      ),
  });

  // Fetch VTM mappings for filter
  const { data: vtmData } = useQuery({
    queryKey: ["vtm_mappings"],
    queryFn: FetchVTMMappings_api,
  });

  // Fetch dealers for filter
  const { data: dealerData } = useQuery({
    queryKey: ["dealers_all"],
    queryFn: () => FetchDealers_api(1, 1000, ""),
  });

  const handleSearch = (values: any) => {
    const newFilters: any = {};

    if (values.startDate) {
      newFilters.startDate = values.startDate.format("YYYY-MM-DD");
    }
    if (values.endDate) {
      newFilters.endDate = values.endDate.format("YYYY-MM-DD");
    }

    if (values.model_id) {
      newFilters.modelId = values.model_id;
    }

    if (values.dealer_id) {
      newFilters.dealerId = values.dealer_id;
    }

    setFilters(newFilters);
  };

  const handleClear = () => {
    form.resetFields();
    setFilters({});
  };

  const handleExportExcel = () => {
    if (!reportData?.data || reportData.data.length === 0) {
      message.warning("No data to export");
      return;
    }

    const headers = [
      "Engine Number",
      "Chassis Number",
      "Model",
      "Dealer",
      "Return Date",
    ];

    const data = reportData.data.map((vehicle: any) => [
      vehicle.engine_number || "N/A",
      vehicle.chassis_number || "N/A",
      `${vehicle.v_type || ""} - ${vehicle.v_modal || ""}`,
      vehicle.dealer_name || "N/A",
      vehicle.return_date && !String(vehicle.return_date).startsWith("0000")
        ? dayjs(vehicle.return_date).format("YYYY-MM-DD")
        : "N/A",
    ]);

    downloadExcel(
      "Returned Vehicles Report",
      headers,
      data,
      `Returned_Vehicles_Report_${dayjs().format("YYYYMMDD")}`,
      undefined,
      undefined,
      undefined,
      `${reportData.data.length} Units`
    );
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case "added":
        return <PlusCircleOutlined style={{ color: "#52c41a" }} />;
      case "instock":
        return <ClockCircleOutlined style={{ color: "#1890ff" }} />;
      case "transfer":
        return <SwapOutlined style={{ color: "#faad14" }} />;
      case "returned":
        return <RollbackOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const expandedRowRender = (record: any) => {
    if (!record.timeline || record.timeline.length === 0) {
      return <Empty description="No timeline data available" />;
    }

    return (
      <div className="p-4 bg-gray-50 rounded">
        <h4 className="font-semibold mb-4">Vehicle Timeline</h4>
        <Timeline>
          {record.timeline.map((event: any, index: number) => (
            <Timeline.Item key={index} dot={getTimelineIcon(event.type)}>
              <div>
                <p className="font-medium mb-1">{event.description}</p>
                <p className="text-sm text-gray-500">
                  {event.date && !String(event.date).startsWith("0000")
                    ? dayjs(event.date).format("MMMM D, YYYY [at] h:mm A")
                    : "N/A"}
                </p>
                {event.remarks && (
                  <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-700">{event.remarks}</p>
                  </div>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </div>
    );
  };

  const columns = [
    {
      title: "Engine Number",
      dataIndex: "engine_number",
      key: "engine_number",
      width: 150,
    },
    {
      title: "Chassis Number",
      dataIndex: "chassis_number",
      key: "chassis_number",
      width: 150,
    },
    {
      title: "Model",
      key: "model",
      render: (record: any) => (
        <div>
          <div className="font-semibold">{record.v_type}</div>
          <div className="text-sm text-gray-500">{record.v_modal}</div>
        </div>
      ),
    },
    {
      title: "Color",
      dataIndex: "color_name",
      key: "color_name",
      render: (color: string) => <Tag color="blue">{color}</Tag>,
    },
    {
      title: "Dealer",
      dataIndex: "dealer_name",
      key: "dealer_name",
    },
    {
      title: "Return Date",
      dataIndex: "return_date",
      key: "return_date",
      render: (date: string) =>
        date ? dayjs(date).format("MMM D, YYYY") : "-",
    },
    {
      title: "Transfers",
      key: "transfers",
      render: (record: any) => {
        const transferCount = record.timeline
          ? record.timeline.filter((t: any) => t.type === "transfer").length
          : 0;
        return (
          <Tag color={transferCount > 0 ? "orange" : "default"}>
            {transferCount}
          </Tag>
        );
      },
    },
  ];

  if (permissionLoading) return <LoadingScreen messageQueue={"Loading..."} />;
  if (!hasPermission) return <NoAccessScreen />;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Returned Vehicles Report</h1>
        <div className="flex gap-3">
          <Button 
            onClick={handleExportExcel} 
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={!reportData?.data || reportData.data.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <Form form={form} layout="inline" onFinish={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Start Date
            </label>
            <Form.Item name="startDate" className="m-0">
              <DatePicker className="w-full h-[38px]" />
            </Form.Item>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              End Date
            </label>
            <Form.Item name="endDate" className="m-0">
              <DatePicker className="w-full h-[38px]" />
            </Form.Item>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Vehicle Model
            </label>
            <Form.Item name="model_id" className="m-0">
              <Select
                placeholder="All Models"
                className="w-full"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {vtmData?.data?.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.v_type} - {item.v_modal}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Source Dealer
            </label>
            <Form.Item name="dealer_id" className="m-0">
              <Select
                placeholder="All Dealers"
                className="w-full"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {dealerData?.dealerData?.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="flex items-end gap-2">
            <Button
              type="primary"
              htmlType="submit"
              loading={isFetchingReport}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 h-[38px] flex-grow"
            >
              <Search className="w-4 h-4 mr-2" />
              <span>Search</span>
            </Button>
            <Button 
              onClick={handleClear}
              className="flex items-center justify-center bg-gray-50 border-gray-100 hover:bg-gray-100 text-gray-400 h-[38px]"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>
        </Form>
      </div>

      {reportData?.data && reportData.data.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">RECLAIMED ASSET VOLUME</p>
                <p className="text-2xl font-bold">{reportData.data.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden mx-6">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <Table
              columns={columns}
              dataSource={reportData?.data || []}
              rowKey="id"
              loading={isFetchingReport}
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                       <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">Asset Lifecycle Audit</h4>
                    </div>
                    {expandedRowRender(record)}
                  </div>
                ),
                expandedRowKeys,
                onExpandedRowsChange: (keys) =>
                  setExpandedRowKeys(keys as number[]),
                expandIcon: ({ expanded, onExpand, record }) => (
                  <button 
                    onClick={e => onExpand(record, e)}
                    className={`transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`}
                  >
                    <ChevronRight className="w-5 h-5 text-orange-500" />
                  </button>
                )
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                style: { padding: '1.5rem 2rem' }
              }}
            />
        </div>
      </div>
    </div>
  );
};
